'use client'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types'
import { _cloneDeep, _get } from 'utils/shared'
import classNames from 'classnames'
import { BorderTextBox } from 'components/BorderTextBox'
import { MediaImageStandard, SuperScript } from 'components/Contentful'
import { VideoDialog } from 'components/VideoDialog'
import { VideoPlayer } from 'components/VideoPlayer'
import { rewriteAssetUrl } from 'utils/conversions'
import { removeSpecialCharacters } from 'utils/shared'
import { ChildComponentGeneratorClient } from 'components/ContentHandlersAppRouter/ChildComponentGeneratorClient'
import dynamic from 'next/dynamic'
import { NO_CRAWL_ID_PLACEHOLDER_REGEX } from 'constants/regex'
import { AchieveLink } from 'components/AchieveLink'
import { DataStripContent } from 'components/DataStripContent/DataStripContent'
import { EditorsNote } from 'components/EditorsNote/EditorsNote'
import { QuickTipContent } from 'components/QuickTipContent/QuickTipContent'
import { ProsAndConsContent } from 'components/ProsAndConsContent/ProsAndConsContent'
import { MarkdownTableContent } from 'components/MarkdownTableContent/MarkdownTableContent'
import { ProductProviderCards } from 'components/ProductProviderCards/ProductProviderCards'
import ButtonCTAv2 from 'components/ButtonCTAv2/index'
import ButtonCTA from 'components/ButtonCTA/index'
import { AdvertisingDisclosures } from 'pageSections/HeroDebt/components/AdvertisingDisclosures'
import { UncrawlableLinkClient } from 'components/Contentful/Typography'
import { sanitizeCmsHtml, sanitizeSvg } from 'utils/sanitize/sanitize'
import { ClientSideTypographyClient } from 'components/Contentful/Typography'
import { HyperLinkClient } from 'components/Contentful/Typography/HyperLinkClient'
import { handleContentToMapper, selectOverride } from 'components/Contentful/Typography/Typography'

const DTICalculator = dynamic(() =>
  import(/* webpackChunkName: "DTICalculator" */ 'pageSections/DTICalculator/DTICalculator', {
    ssr: false,
  })
)
const PopUpSection = dynamic(() =>
  import(/* webpackChunkName: "PopUpSection" */ 'pageSections/PopUpSection/PopUpSection', {
    ssr: false,
  })
)

const Calculator = dynamic(() =>
  import(/* webpackChunkName: "HelocCalculatorSection" */ 'components/Calculator', { ssr: false })
)

const CardAppCTA = dynamic(() =>
  import(/* webpackChunkName: "CardAppCTA" */ 'components/Cards/CardAppCTA/CardAppCTA', {
    ssr: false,
  })
)

const CardBottomCTA = dynamic(() =>
  import(/* webpackChunkName: "CardBottomCTA" */ 'components/Cards/CardBottomCTA/CardBottomCTA', {
    ssr: false,
  })
)

const CardCTA = dynamic(() =>
  import(/* webpackChunkName: "CardCTA" */ 'components/Cards/CardCTA/CardCTA', { ssr: false })
)

// ─── Rich Text variant/component mapping (same keys as the MUI version) ───
const RICH_TEXT = {
  [BLOCKS.PARAGRAPH]: { variant: 'labelMd', component: 'p' },
  [BLOCKS.HEADING_1]: { variant: 'headingLg', component: 'h1' },
  [BLOCKS.HEADING_2]: { variant: 'headingMd', component: 'h2', fontWeight: 'medium' },
  [BLOCKS.HEADING_3]: { variant: 'bodyLg', component: 'h3', fontWeight: 'medium' },
  [BLOCKS.HEADING_4]: { variant: 'labelMd', component: 'h4', fontWeight: 'medium' },
  [BLOCKS.HEADING_5]: { variant: 'bodyXs', component: 'h5' },
  [BLOCKS.HEADING_6]: { variant: 'bodyXs', component: 'h6' },
  [BLOCKS.OL_LIST]: { component: 'ol' },
  [BLOCKS.UL_LIST]: { component: 'ul' },
  [BLOCKS.LIST_ITEM]: { component: 'li' },
  [BLOCKS.HR]: {},
  [BLOCKS.EMBEDDED_ASSET]: {},
  [BLOCKS.TABLE]: { variant: 'bodyLg', component: 'table' },
  [BLOCKS.TABLE_ROW]: { variant: 'bodyLg', component: 'tr' },
  [BLOCKS.TABLE_CELL]: { variant: 'bodyLg', component: 'td' },
  [BLOCKS.TABLE_HEADER_CELL]: { variant: 'bodyLg', component: 'th' },
  [INLINES.HYPERLINK]: { component: 'a' },
  [INLINES.ASSET_HYPERLINK]: { component: 'a' },
}

// ─── Font-weight Tailwind map ──────────────────────────────────────────────
const FONT_WEIGHT_MAP = {
  light: 'font-light',
  regular: 'font-normal',
  medium: 'font-medium',
  bold: 'font-bold',
}

// ─── Helper: split line-breaks the same way the MUI version does ───────────
const parseRichTextLineBreak = (children) => {
  if (Array.isArray(children) && children.length === 1 && children[0] === '') {
    return ['\u00A0']
  }

  function splitBreakLineInline(inputArray) {
    if (!Array.isArray(inputArray)) return inputArray
    const resultArray = []
    inputArray.forEach((item, itemIndex) => {
      if (typeof item === 'string') {
        if (item?.includes('\n')) {
          const splitItems = item.split('\n')
          splitItems.forEach((splitItem, index) => {
            resultArray.push(splitItem)
            if (index < splitItems.length - 1) {
              resultArray.push(<br key={`br-${itemIndex}-${index}`} />)
            }
          })
        } else {
          resultArray.push(item)
        }
      } else {
        resultArray.push(item)
      }
    })
    return resultArray
  }

  return splitBreakLineInline(children).map((child) =>
    typeof child === 'string' ? child.replaceAll(/[\u2028\u2029]/g, '') : child
  )
}

// ─── Embedded page-section helper (unchanged) ──────────────────────────────
const embeddedPageSection = (node, contentType) => {
  const pageSection = _get(node, 'data.target')
  return (
    <ChildComponentGeneratorClient
      contentTypes={{ [pageSection?.fields?.uiComponent]: contentType }}
      uiComponent={pageSection?.fields?.uiComponent}
      {...pageSection}
      key={pageSection?.sys?.id}
    />
  )
}

// ─── MUI-free render-node: outputs a plain HTML element with Tailwind classes ─
function createRenderNodeNoMui({
  children,
  blockType,
  variantToChild = false,
  variantOverride,
  className,
  richText = RICH_TEXT,
  propsComponent,
}) {
  let childWithParentProps = children
  if (variantToChild) {
    childWithParentProps = _cloneDeep(children)
    childWithParentProps.forEach((child) => {
      if (Object.hasOwnProperty.call(child, 'props')) {
        child.props.variant = selectOverride(
          BLOCKS[blockType],
          variantOverride,
          'variant',
          richText
        )
      }
    })
  }

  const variant = selectOverride(BLOCKS[blockType], variantOverride, 'variant', richText)
  const Component = richText[BLOCKS[blockType]].component || 'p'
  const fontWeightClass =
    FONT_WEIGHT_MAP[propsComponent?.fontWeight] ||
    FONT_WEIGHT_MAP[richText[BLOCKS[blockType]]?.fontWeight] ||
    ''

  // Strip MUI-only props so they don't leak into the DOM
  const { 'no-crawl-id': noCrawlId, ...restProps } = propsComponent || {}
  const domSafeProps = { ...restProps }
  delete domSafeProps.fontWeight
  delete domSafeProps.sx
  delete domSafeProps.component
  delete domSafeProps.variant

  const classes = classNames(variant, fontWeightClass, className, domSafeProps.className)

  return (
    <Component
      {...domSafeProps}
      className={classes}
      {...(noCrawlId ? { 'no-crawl-id': noCrawlId } : {})}
    >
      {parseRichTextLineBreak(childWithParentProps)}
    </Component>
  )
}

/**
 * MUI-free TypographyLongForm — renders Contentful Rich Text using only
 * Tailwind utility classes. Drop-in replacement for the Ascend-based
 * TypographyLongForm in contexts where MUI styling must be avoided.
 *
 * @param {Object}  content          Rich-text content from Contentful
 * @param {Object}  variantOverride  Map of rich-text block types → Tailwind variant class names
 * @param {Object}  styleOverride    Kept for API compatibility (ignored — use className instead)
 */
function TypographyLongForm({
  content: typographyContent = '',
  variantOverride,
  styleOverride,
  ...props
}) {
  // ── String content shortcut ───────────────────────────────────────────
  if (typeof typographyContent === 'string') {
    const variant = selectOverride(BLOCKS.PARAGRAPH, variantOverride, 'variant', RICH_TEXT)
    const domSafeProps = { ...props }
    delete domSafeProps.sx
    delete domSafeProps.component
    delete domSafeProps.variant
    delete domSafeProps.fontWeight
    return (
      <p
        {...domSafeProps}
        className={classNames(
          variant,
          'font-normal mb-[20px] scroll-mt-[72px] md:text-sm leading-[170%] md:leading-[170%] [&_a]:[font-size:inherit]',
          props.className
        )}
      >
        {typographyContent}
      </p>
    )
  }

  // ── Object / Rich-text content ────────────────────────────────────────
  const { textContent } = handleContentToMapper(typographyContent)

  const createRenderNode = (
    node,
    children,
    blockType,
    className = 'typography-default',
    variantToChild = false,
    // styleToChild kept in signature for positional API compatibility with the MUI version
    styleToChild = false,
    extraProps = {}
  ) => {
    void styleToChild // Not used in the MUI-free version
    const fallbackTestId = _get(node, 'content[0].value')
      ? `${_get(node, 'content[0].value')}`.slice(0, 50)
      : 'no-id-needed'

    const fallbackId = _get(props, 'id')
      ? _get(props, 'id')
      : `${BLOCKS[blockType]}-${(_get(node, 'content[0].value') ?? '')
          .slice(0, 50)
          .replaceAll(' ', '-')}`

    const dataTestId = extraProps['data-testid'] ?? fallbackTestId
    const id = extraProps.id ?? fallbackId

    const propsComponent = {
      ['data-testid']: dataTestId,
      fontWeight: RICH_TEXT[BLOCKS[blockType]].fontWeight,
      id: removeSpecialCharacters(id),
      'no-crawl-id': extraProps['no-crawl-id'] ?? undefined,
      ...props,
    }

    return createRenderNodeNoMui({
      children,
      blockType,
      variantToChild,
      variantOverride,
      className,
      richText: RICH_TEXT,
      propsComponent,
    })
  }

  // ── Render options ────────────────────────────────────────────────────
  const options = {
    renderMark: {
      [MARKS.BOLD]: (text) => <strong>{text}</strong>,
      [MARKS.ITALIC]: (text) => <em>{text}</em>,
    },
    renderNode: {
      // ── Block nodes ─────────────────────────────────────────────────
      [BLOCKS.PARAGRAPH]: (node, children) => {
        const onlyOneText =
          Array.isArray(node.content) &&
          node.content.length === 1 &&
          node.content[0]?.nodeType === 'text' &&
          typeof node.content[0]?.value === 'string'

        if (onlyOneText) {
          const val = node.content[0].value
          const m = val.match(NO_CRAWL_ID_PLACEHOLDER_REGEX)
          if (m) {
            return createRenderNode(
              node,
              [],
              'PARAGRAPH',
              'font-normal mb-[20px] scroll-mt-[72px] md:text-sm leading-[170%] md:leading-[170%] [&_a]:[font-size:inherit]',
              false,
              false,
              {
                ['data-testid']: m[0],
                id: m[0],
                ['no-crawl-id']: m[0],
              }
            )
          }
        }

        return createRenderNode(
          node,
          children,
          'PARAGRAPH',
          'font-normal mb-[20px] scroll-mt-[72px] md:text-sm leading-[170%] md:leading-[170%] [&_a]:[font-size:inherit]'
        )
      },

      [BLOCKS.HEADING_1]: (node, children) =>
        createRenderNode(node, children, 'HEADING_1', 'mb-[20px] scroll-mt-[72px]', true),
      [BLOCKS.HEADING_2]: (node, children) =>
        createRenderNode(node, children, 'HEADING_2', 'mt-10 mb-[20px] scroll-mt-[72px]', true),
      [BLOCKS.HEADING_3]: (node, children) =>
        createRenderNode(node, children, 'HEADING_3', 'mt-10 mb-[20px] scroll-mt-[72px]', true),
      [BLOCKS.HEADING_4]: (node, children) =>
        createRenderNode(
          node,
          children,
          'HEADING_4',
          'mt-10 mb-[20px] scroll-mt-[72px] leading-[170%]!',
          true
        ),
      [BLOCKS.HEADING_5]: (node, children) =>
        createRenderNode(node, children, 'HEADING_5', 'mb-10 scroll-mt-[72px]', true),
      [BLOCKS.HEADING_6]: (node, children) =>
        createRenderNode(node, children, 'HEADING_6', 'mb-10 scroll-mt-[72px]', true),

      // ── Lists ───────────────────────────────────────────────────────
      [BLOCKS.OL_LIST]: (_node, children) => (
        <ol
          className={
            'typography-ol list-decimal mb-8 [&>li>ol]:list-[upper-alpha] [&>li>ol>li>ol]:list-[lower-roman] [&>li>ol>li>ol>li>ol]:list-[lower-alpha] [&_li_p:only-of-type]:mb-0 [&_li_.typography-ol]:mb-0 [&_li_.typography-ul]:mb-0'
          }
        >
          {children}
        </ol>
      ),
      [BLOCKS.UL_LIST]: (_node, children) => (
        <ul className={'typography-ul mb-8 [&_li_.typography-ul]:mb-0 [&_li_.typography-ol]:mb-0'}>
          {children}
        </ul>
      ),
      [BLOCKS.LIST_ITEM]: (_node, children) => (
        <li className={'mb-1 [&_li_p:last-child]:mb-0'}>{children}</li>
      ),

      // ── HR ──────────────────────────────────────────────────────────
      [BLOCKS.HR]: () => <hr className="mb-8 border-t border-gray-300" />,

      // ── Embedded asset (image) ──────────────────────────────────────
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const imageWidth = _get(node, 'data.target.fields.file.details.image.width')
        const imageHeight = _get(node, 'data.target.fields.file.details.image.height')
        return (
          <span className={'items-center flex justify-center'}>
            <MediaImageStandard
              content={node?.data.target}
              width={imageWidth}
              height={imageHeight}
              className={'object-contain h-full w-full'}
              {...props}
            />
          </span>
        )
      },

      // ── Tables (plain HTML instead of MUI Table) ────────────────────
      [BLOCKS.TABLE]: (node, children) => {
        const variant = selectOverride(BLOCKS.TABLE, variantOverride, 'variant', RICH_TEXT)
        return (
          <div
            className={classNames(
              'overflow-x-auto border-collapse h-auto mb-8 [&_td_p:last-child]:mb-0 [&_th_p:last-child]:mb-0',
              node?.content[0]?.content?.length < 4 &&
                'md:[&_td]:w-auto md:[&_th]:w-auto [&_td]:text-center [&_th]:text-center'
            )}
          >
            <table className={classNames('table-auto w-full', variant)}>{children}</table>
          </div>
        )
      },
      [BLOCKS.TABLE_ROW]: (node, children) => {
        const childrenNodeTypes = node?.content?.map((c) => c?.nodeType) ?? []
        if (childrenNodeTypes.includes(BLOCKS.TABLE_HEADER_CELL)) {
          return (
            <thead>
              <tr>{children}</tr>
            </thead>
          )
        }
        return (
          <tbody>
            <tr>{children}</tr>
          </tbody>
        )
      },
      [BLOCKS.TABLE_CELL]: (_node, children) => (
        <td className="h-auto border border-solid border-[#DCDCDC] border-collapse p-2">
          {children}
        </td>
      ),
      [BLOCKS.TABLE_HEADER_CELL]: (_node, children) => (
        <th className="h-auto border border-solid border-[#DCDCDC] border-collapse p-2 font-medium">
          {children}
        </th>
      ),

      // ── Inline hyperlinks ───────────────────────────────────────────
      [INLINES.HYPERLINK]: (node, children) => {
        return (
          <HyperLinkClient
            node={node}
            props={props}
            styleOverride={styleOverride}
            variantOverride={variantOverride}
            richText={RICH_TEXT}
          >
            {children}
          </HyperLinkClient>
        )
      },

      [INLINES.ASSET_HYPERLINK]: (node, children) => {
        const linkId = node?.content[0]?.value
        const url = rewriteAssetUrl(node?.data?.target?.fields?.file.url || node?.data.url)
        return (
          <a
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`Link-${url}-${linkId}`}
            href={url}
            aria-label={`${children} - link opens in a new tab`}
            className="p-0 scroll-mt-[72px] underline text-blue-500 hover:text-blue-700"
          >
            {children}
          </a>
        )
      },

      // ── Inline embedded entries ─────────────────────────────────────
      [INLINES.EMBEDDED_ENTRY]: (node) => {
        const {
          plainText,
          styledText,
          variation,
          identifier,
          videoPlayer,
          uiComponent,
          fieldName,
        } = _get(node, 'data.target.fields', {})
        const { contentType } = _get(node, 'data.target.sys', {})

        if (contentType?.sys?.id === 'pageSection' && uiComponent === 'Pop-Up') {
          return embeddedPageSection(node, PopUpSection)
        }
        if (contentType?.sys?.id === 'pageSection' && uiComponent === 'DTICalculator') {
          return embeddedPageSection(node, DTICalculator)
        }
        if (contentType?.sys?.id === 'calculator') {
          const { fields } = _get(node, 'data.target', {})
          return <Calculator content={fields} />
        }
        if (contentType?.sys?.id === 'articleCtaCard') {
          const { fields } = _get(node, 'data.target', {})
          return <CardCTA content={fields} trackingName={'In-Content'} />
        }
        if (fieldName === 'advertisingDisclosures') {
          return <AdvertisingDisclosures />
        }
        if (uiComponent === 'CardAppCTA') {
          const { fields } = _get(node, 'data.target', {})
          return <CardAppCTA content={fields} />
        }
        if (uiComponent === 'CardBottomCTA') {
          const { fields } = _get(node, 'data.target', {})
          return <CardBottomCTA content={fields} />
        }
        if (styledText === 'superscript') {
          return <SuperScript text={plainText} variation={variation} {...props} />
        }
        if (styledText === 'scrollTo') {
          return <span data-scrolto={identifier}></span>
        }
        if (styledText === 'scrollFrom') {
          return (
            <a
              data-testid={`Scroll-to-${plainText}`}
              onClick={() => {
                const el = document.querySelector(`[data-scrolto="${identifier}"]`)
                if (el) {
                  window.scrollTo({ top: el.offsetTop - 72, behavior: 'smooth' })
                }
              }}
              aria-label={`Scroll to ${plainText}`}
              className="cursor-pointer underline text-blue-500 hover:text-blue-700"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  const el = document.querySelector(`[data-scrolto="${identifier}"]`)
                  if (el) {
                    window.scrollTo({ top: el.offsetTop - 72, behavior: 'smooth' })
                  }
                }
              }}
            >
              {plainText}
            </a>
          )
        }
        if (videoPlayer) {
          const { target } = _get(node, 'data', {})
          return <VideoDialog content={target} />
        }
        if (uiComponent === 'VideoPlayer') {
          const target = _get(node, 'data.target.fields', {})
          return <VideoPlayer content={target} frameClassName={'w-full h-auto'} frameEmbed={true} />
        }
        if (contentType?.sys?.id === 'uncrawlableLink') {
          return (
            <UncrawlableLinkClient
              node={node}
              styleOverride={styleOverride}
              variantOverride={variantOverride}
            />
          )
        }
        if (uiComponent === 'Typography') {
          const { fields } = _get(node, 'data.target', {})
          return (
            <TypographyLongForm
              {...props}
              component="span"
              content={fields?.textContent}
              variantOverride={variantOverride}
              styleOverride={styleOverride}
            />
          )
        }
        if (uiComponent === 'TypographyClientSide') {
          const { fields } = _get(node, 'data.target', {})
          return (
            <ClientSideTypographyClient
              textContent={fields?.textContent}
              variantOverride={variantOverride}
              styleOverride={styleOverride}
              TypographyComponent={TypographyLongForm}
              {...props}
            />
          )
        }
        if (node?.data?.uiComponent === 'TypographyClientSide') {
          const data = _get(node, 'data.textContent', {})
          return (
            <ClientSideTypographyClient
              textContent={data}
              variantOverride={variantOverride}
              styleOverride={styleOverride}
              TypographyComponent={TypographyLongForm}
              {...props}
            />
          )
        }
        return null
      },

      // ── Block embedded entries ──────────────────────────────────────
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const {
          plainText,
          styledText,
          variation,
          identifier,
          videoPlayer,
          uiComponent,
          fieldName,
          textContent,
          name,
          variant,
        } = _get(node, 'data.target.fields', {})
        const { contentType } = _get(node, 'data.target.sys', {})

        if (contentType?.sys?.id === 'pageSection' && uiComponent === 'Pop-Up') {
          return embeddedPageSection(node, PopUpSection)
        }
        if (contentType?.sys?.id === 'pageSection' && uiComponent === 'DTICalculator') {
          return embeddedPageSection(node, DTICalculator)
        }
        if (contentType?.sys?.id === 'calculator') {
          const { fields } = _get(node, 'data.target', {})
          return <Calculator content={fields} />
        }
        if (fieldName === 'advertisingDisclosures') {
          return <AdvertisingDisclosures />
        }
        if (fieldName == 'editorsNote') {
          const { fields } = _get(node, 'data.target', {})
          return <EditorsNote {...fields} />
        }
        if (fieldName == 'quickTip') {
          return <QuickTipContent content={textContent} name={name} />
        }
        if (contentType?.sys?.id === 'articleCtaCard') {
          const { fields } = _get(node, 'data.target', {})
          return <CardCTA content={fields} trackingName={'In-Content'} />
        }
        if (uiComponent === 'CardAppCTA') {
          const { fields } = _get(node, 'data.target', {})
          return <CardAppCTA content={fields} />
        }
        if (uiComponent === 'CardBottomCTA') {
          const { fields } = _get(node, 'data.target', {})
          return <CardBottomCTA content={fields} />
        }
        if (styledText === 'superscript') {
          return <SuperScript text={plainText} variation={variation} {...props} />
        }
        if (styledText === 'scrollTo') {
          return <span data-scrolto={identifier}></span>
        }
        if (uiComponent === 'MediaField') {
          const imageWidth = _get(
            node,
            'data.target.fields.mediaContent.fields.file.details.image.width'
          )
          const imageHeight = _get(
            node,
            'data.target.fields.mediaContent.fields.file.details.image.height'
          )
          const imageContent = _get(node, 'data.target.fields.mediaContent')
          const href = _get(node, 'data.target.fields.href')
          return (
            <span className={'items-center flex justify-center'}>
              {href ? (
                <AchieveLink
                  href={href}
                  track={{
                    click_id: `MediaField-TypograpyLongForm`,
                    event_action: 'link_click',
                    event_type: 'link_click',
                    click_url: href,
                  }}
                  data-testid={'media-link'}
                >
                  <MediaImageStandard
                    content={imageContent}
                    width={imageWidth}
                    height={imageHeight}
                    className={'object-contain h-full w-full'}
                    {...props}
                  />
                </AchieveLink>
              ) : (
                <MediaImageStandard
                  content={imageContent}
                  width={imageWidth}
                  height={imageHeight}
                  className={'object-contain h-full w-full'}
                  {...props}
                />
              )}
            </span>
          )
        }
        if (styledText === 'scrollFrom') {
          return (
            <a
              data-testid={`Scroll-to-${plainText}`}
              onClick={() => {
                const el = document.querySelector(`[data-scrolto="${identifier}"]`)
                if (el) {
                  window.scrollTo({ top: el.offsetTop - 72, behavior: 'smooth' })
                }
              }}
              aria-label={`Scroll to ${plainText}`}
              className="cursor-pointer underline text-blue-500 hover:text-blue-700"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  const el = document.querySelector(`[data-scrolto="${identifier}"]`)
                  if (el) {
                    window.scrollTo({ top: el.offsetTop - 72, behavior: 'smooth' })
                  }
                }
              }}
            >
              {plainText}
            </a>
          )
        }
        if (videoPlayer) {
          const { target } = _get(node, 'data', {})
          return <VideoDialog content={target} />
        }
        if (uiComponent === 'VideoPlayer') {
          const target = _get(node, 'data.target.fields', {})
          return (
            <VideoPlayer
              content={target}
              frameClassName={'w-full h-auto'}
              frameEmbed={true}
              aspectRatio={16 / 9}
            />
          )
        }
        if (contentType?.sys?.id === 'uncrawlableLink') {
          return (
            <UncrawlableLinkClient
              node={node}
              styleOverride={styleOverride}
              variantOverride={variantOverride}
            />
          )
        }
        if (uiComponent === 'Typography') {
          const { fields } = _get(node, 'data.target', {})
          return (
            <TypographyLongForm
              {...props}
              content={fields?.textContent}
              variantOverride={variantOverride}
              styleOverride={styleOverride}
            />
          )
        }
        if (uiComponent === 'TypographyClientSide') {
          const { fields } = _get(node, 'data.target', {})
          return (
            <ClientSideTypographyClient
              textContent={fields?.textContent}
              variantOverride={variantOverride}
              styleOverride={styleOverride}
              TypographyComponent={TypographyLongForm}
              {...props}
            />
          )
        }
        if (node?.data?.uiComponent === 'TypographyClientSide') {
          const data = _get(node, 'data.textContent', {})
          return (
            <ClientSideTypographyClient
              textContent={data}
              variantOverride={variantOverride}
              styleOverride={styleOverride}
              TypographyComponent={TypographyLongForm}
              {...props}
            />
          )
        }
        if (fieldName == 'textFooterRemarkable') {
          return <BorderTextBox content={textContent} variant={variant} id={name} />
        }
        if (contentType?.sys?.id === 'dataStrip') {
          const { target } = _get(node, 'data', {})
          return <DataStripContent {...target} />
        }
        if (contentType?.sys?.id === 'iframe') {
          const { content } = _get(node, 'data.target.fields', {})
          return (
            <div className="chart" dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(content) }} />
          )
        }
        if (contentType?.sys?.id === 'prosAndCons') {
          const { fields } = _get(node, 'data.target', {})
          return <ProsAndConsContent content={fields} />
        }
        if (contentType?.sys?.id === 'table') {
          const { fields } = _get(node, 'data.target', {})
          return <MarkdownTableContent content={fields} />
        }
        if (contentType?.sys?.id === 'productProviderCards') {
          const { fields } = _get(node, 'data.target', {})
          return <ProductProviderCards content={fields} />
        }
        return null
      },

      // ── Custom nodes (sliderCTA, buttonCTAv2, buttonCTA) ────────────
      sliderCTA: ({ data }) => {
        const fields = {
          name: 'CTA 0 - Bills Article',
          title: {
            data: {},
            content: [
              {
                data: {},
                content: [{ data: {}, marks: [], value: data?.title, nodeType: 'text' }],
                nodeType: 'heading-3',
              },
            ],
            nodeType: 'document',
          },
          subtitle: {
            data: {},
            content: [
              {
                data: {},
                content: [
                  {
                    data: {},
                    marks: [],
                    value: 'Or speak to a debt consultant ',
                    nodeType: 'text',
                  },
                  {
                    data: { uri: `tel:${data?.phone}` },
                    content: [{ data: {}, marks: [], value: data?.phone, nodeType: 'text' }],
                    nodeType: 'hyperlink',
                  },
                ],
                nodeType: 'paragraph',
              },
            ],
            nodeType: 'document',
          },
          link: {
            metadata: { tags: [], concepts: [] },
            sys: {
              space: { sys: { type: 'Link', linkType: 'Space', id: '80jeq2aeq4l1' } },
              id: '17qhOD8ZVhACmel3B48HYh',
              type: 'Entry',
              createdAt: '2025-09-25T03:55:02.662Z',
              updatedAt: '2025-09-25T03:58:13.128Z',
              environment: { sys: { id: 'dev', type: 'Link', linkType: 'Environment' } },
              publishedVersion: 6,
              revision: 2,
              contentType: { sys: { type: 'Link', linkType: 'ContentType', id: 'lInk' } },
              locale: 'en-US',
            },
            fields: {
              name: 'Get Started - Bills Link',
              uiComponent: 'LinkButton',
              fieldName: 'billsArticleCtaLink',
              linkText: {
                data: {},
                content: [
                  {
                    data: {},
                    content: [{ data: {}, marks: [], value: data?.btnLabel, nodeType: 'text' }],
                    nodeType: 'paragraph',
                  },
                ],
                nodeType: 'document',
              },
              linkHref: data?.url,
            },
          },
        }
        return <CardCTA content={fields} trackingName={'In-Content'} />
      },
      buttonCTAv2: ({ data }) => (
        <ButtonCTAv2
          title={data?.title}
          items={data?.items}
          url={data?.url}
          buttonText={data?.buttonText}
          underButtonText={data?.underButtonText}
        />
      ),
      buttonCTA: ({ data }) => (
        <ButtonCTA
          description={data?.description}
          href={data?.url}
          label={data?.label}
          location={data?.location}
        />
      ),
    },

    renderText: (text) => {
      if (
        typeof text === 'string' &&
        (text.includes('<svg') || text.includes('<b>') || text.includes('<strong>'))
      ) {
        const parts = []
        let key = 0
        const htmlRegex = /(<svg[\s\S]*?<\/svg>|<b>[\s\S]*?<\/b>|<strong>[\s\S]*?<\/strong>)/g
        const segments = text.split(htmlRegex)

        segments.forEach((segment) => {
          const trimmed = segment.trim()
          if (trimmed.startsWith('<svg')) {
            parts.push(
              <span
                key={`svg-${key++}`}
                className="inline-flex items-center align-middle mx-1"
                dangerouslySetInnerHTML={{ __html: sanitizeSvg(segment) }}
              />
            )
          } else if (trimmed.startsWith('<b>') || trimmed.startsWith('<strong>')) {
            const boldText = segment.replace(/<\/?b>|<\/?strong>/g, '')
            parts.push(<strong key={`bold-${key++}`}>{boldText}</strong>)
          } else if (segment) {
            parts.push(segment)
          }
        })

        return parts.length > 0 ? parts : text
      }
      return text
    },
  }

  return documentToReactComponents(textContent, options)
}

export default TypographyLongForm
export { TypographyLongForm, RICH_TEXT }
